// A ledger entry can carry up to 5 proofs, stored as a JSON array of URLs in
// the existing financial_transactions.proof TEXT column.
//
// Storing a list in the column rather than adding proof_2..proof_5 or a child
// table means no migration and no backend change at all: the compliance check
// only tests truthiness, and update_transaction's "did the proof change?" check
// is a string compare that still works because serialisation is deterministic.
// Rows written before this existed hold a bare URL, so every read goes through
// parseProofs(), which treats that as a one-item list.

export const MAX_PROOFS = 5;

const IMAGE_EXT = /\.(jpe?g|png|gif|webp|bmp|heic|heif|avif|svg)(\?|#|$)/i;

/** '' / null → []; a bare URL → [url]; a JSON array → the array. Never throws. */
export function parseProofs(value?: string | null): string[] {
  if (!value) return [];
  const raw = value.trim();
  if (!raw) return [];
  if (raw.startsWith('[')) {
    try {
      const parsed = JSON.parse(raw);
      if (Array.isArray(parsed)) {
        return parsed.filter((u): u is string => typeof u === 'string' && !!u.trim());
      }
    } catch {
      // Malformed JSON — fall through and treat the whole thing as one URL
      // rather than losing the reference entirely.
    }
  }
  return [raw];
}

/**
 * [] → null, so an empty list is stored as NULL and never as the truthy
 * string "[]" (which would satisfy the debit-compliance check). Order is
 * preserved — never sort here, or the edit-justification compare breaks.
 */
export function serializeProofs(urls: string[]): string | null {
  const clean = urls.filter((u) => !!u && !!u.trim()).slice(0, MAX_PROOFS);
  if (clean.length === 0) return null;
  return JSON.stringify(clean);
}

/** Images go to the lightbox; everything else (PDFs mostly) opens in a new tab. */
export function isImageUrl(url: string): boolean {
  return IMAGE_EXT.test(url);
}
